/**
 * beseen — Dawn Parity Facets Controller (iOS/Android/Desktop SAFE)
 * Works with your facets.liquid + price-facet markup:
 * - #FacetFiltersForm, #FacetFiltersFormMobile
 * - .active-facets-desktop / .active-facets-mobile
 * - #ProductGridContainer
 * - #mob_sort_by_overlay + .facet-sort-option
 *
 * Emits events after update:
 * - collection:updated
 * - beseen:collection:reset
 */

(() => {
    "use strict";

    /* -----------------------------
      Small helpers
    ----------------------------- */
    const safe = (fn) => {
        try { return fn(); } catch (e) { console.error("[beseen Facets]", e); }
    };

    const debounce = (fn, delay = 250) => {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn(...args), delay);
        };
    };

    // iOS safe requestIdleCallback polyfill
    if (!("requestIdleCallback" in window)) {
        window.requestIdleCallback = (cb) =>
            setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 16 }), 1);
        window.cancelIdleCallback = (id) => clearTimeout(id);
    }

    const raf2 = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

    /* -----------------------------
      Selectors (your markup)
    ----------------------------- */
    const SEL = {
        gridContainer: "#ProductGridContainer",
        desktopForm: "#FacetFiltersForm",
        mobileForm: "#FacetFiltersFormMobile",
        activeDesktop: ".active-facets-desktop",
        activeMobile: ".active-facets-mobile",
        count: "#ProductCount",
        countDesktop: "#ProductCountDesktop",
        spinner: ".loading__spinner",

        // Mobile drawer in your snippet:
        mobileDrawerDetails: ".mobile-facets__disclosure", // <details> wrapper
        mobileInner: ".mobile-facets__inner",

        // Facet details open state:
        desktopDetails: ".facets__wrapper details.js-filter, details.facets__disclosure-vertical.js-filter, details.facets__disclosure.js-filter",
        mobileDetails: ".mobile-facets__details.js-filter",

        // Sort overlay
        sortOverlay: "#mob_sort_by_overlay",
        sortOption: ".facet-sort-option",
        sortSelect: "select[name='sort_by']",
        sortOpenButton: ".mobile-facets__sort",
        sortCloseButton: "#close-sort-by",
    };

    /* -----------------------------
      State store (single source of truth)
    ----------------------------- */
    const STATE = {
        // URL params currently applied
        params: window.location.search.replace(/^\?/, ""),

        // which details are open (keys are element IDs)
        openDesktop: new Set(),
        openMobile: new Set(),

        // mobile drawer open?
        mobileDrawerOpen: false,

        // request mgmt
        abort: null,
        reqId: 0,

        // cache
        cache: new Map(), // url -> html
        cacheLimit: 15,
    };

    const cacheSet = (k, v) => {
        STATE.cache.set(k, v);
        if (STATE.cache.size > STATE.cacheLimit) {
            // delete oldest
            const firstKey = STATE.cache.keys().next().value;
            STATE.cache.delete(firstKey);
        }
    };

    /* -----------------------------
      Utilities: read/restore open state
    ----------------------------- */
    const keyOf = (el) => el?.id || el?.getAttribute?.("data-index") || null;

    const captureOpenState = () => {
        STATE.openDesktop.clear();
        STATE.openMobile.clear();

        document.querySelectorAll(SEL.desktopDetails).forEach((d) => {
            const k = keyOf(d);
            if (k && d.open) STATE.openDesktop.add(k);
        });

        document.querySelectorAll(SEL.mobileDetails).forEach((d) => {
            const k = keyOf(d);
            if (k && d.open) STATE.openMobile.add(k);
        });

        const drawer = document.querySelector(SEL.mobileDrawerDetails);
        STATE.mobileDrawerOpen = !!drawer?.open;
    };

    const restoreOpenState = () => {
        document.querySelectorAll(SEL.desktopDetails).forEach((d) => {
            const k = keyOf(d);
            if (!k) return;
            if (STATE.openDesktop.has(k)) d.open = true;
        });

        document.querySelectorAll(SEL.mobileDetails).forEach((d) => {
            const k = keyOf(d);
            if (!k) return;
            if (STATE.openMobile.has(k)) d.open = true;
        });

        const drawer = document.querySelector(SEL.mobileDrawerDetails);
        if (drawer && STATE.mobileDrawerOpen) {
            drawer.open = true;
            // lock scroll (Dawn does via body class/overflow; we keep minimal)
            //   document.body.classList.add("overflow-hidden");
        }
    };

    /* -----------------------------
      Sections resolver (Dawn style)
    ----------------------------- */
    const getSections = () => {
        const sections = [];

        const gridSection = document.getElementById("product-grid")?.dataset?.id;
        if (gridSection) sections.push(gridSection);

        const filtersSection =
            document.querySelector("#main-collection-filters")?.dataset?.id ||
            document.querySelector("[data-facets-section-id]")?.getAttribute("data-facets-section-id") ||
            document.querySelector(".facets-container")?.closest("[data-section-id]")?.getAttribute("data-section-id") ||
            document.querySelector("facet-filters-form")?.closest("[data-section-id]")?.getAttribute("data-section-id");

        if (filtersSection && filtersSection !== gridSection) sections.push(filtersSection);

        // unique
        return [...new Set(sections)].filter(Boolean);
    };

    /* -----------------------------
      DOM patch (only what we need)
    ----------------------------- */
    const patchHTML = (doc) => {
        // Grid
        const freshGrid = doc.querySelector(SEL.gridContainer);
        const curGrid = document.querySelector(SEL.gridContainer);
        if (freshGrid && curGrid) curGrid.innerHTML = freshGrid.innerHTML;

        // Desktop facets form
        const freshDesktop = doc.querySelector(SEL.desktopForm);
        const curDesktop = document.querySelector(SEL.desktopForm);
        if (freshDesktop && curDesktop) curDesktop.innerHTML = freshDesktop.innerHTML;

        // Mobile facets form
        const freshMobile = doc.querySelector(SEL.mobileForm);
        const curMobile = document.querySelector(SEL.mobileForm);
        if (freshMobile && curMobile) curMobile.innerHTML = freshMobile.innerHTML;

        // Active facets + counts (if present)
        [SEL.activeDesktop, SEL.activeMobile, SEL.count, SEL.countDesktop].forEach((sel) => {
            const f = doc.querySelector(sel);
            const c = document.querySelector(sel);
            if (f && c) c.innerHTML = f.innerHTML;
        });
    };

    const setLoading = (on) => {
        const grid = document.querySelector(SEL.gridContainer);
        if (on) grid?.classList.add("loading");
        else grid?.classList.remove("loading");

        document.querySelectorAll(SEL.spinner).forEach((s) => {
            if (on) s.classList.remove("hidden");
            else s.classList.add("hidden");
        });
    };

    /* -----------------------------
      URL + form params builder
    ----------------------------- */
    const serializeForm = (form) => {
        if (!form) return "";
        const fd = new FormData(form);
        return new URLSearchParams(fd).toString();
    };

    const collectParams = () => {
        // merge both forms like Dawn
        const forms = Array.from(document.querySelectorAll("facet-filters-form form"));
        const parts = forms.map(serializeForm).filter(Boolean);
        return parts.join("&");
    };

    const updateURL = (params) => {
        const url = `${location.pathname}${params ? "?" + params : ""}`;
        history.pushState({ searchParams: params }, "", url);
    };

    /* -----------------------------
      Fetch pipeline (abort + dedupe + cache)
    ----------------------------- */
    const fetchSection = async (sectionId, params, signal) => {
        const url = `${location.pathname}?section_id=${sectionId}${params ? "&" + params : ""}`;

        if (STATE.cache.has(url)) return STATE.cache.get(url);

        const res = await fetch(url, {
            signal,
            headers: { "X-Requested-With": "XMLHttpRequest" },
            credentials: "same-origin",
        });
        const html = await res.text();
        cacheSet(url, html);
        return html;
    };

    const render = async (params, { updateHistory = true } = {}) => {
        // prevent pointless renders
        params = (params || "").trim();

        // capture UI state BEFORE DOM replacement
        captureOpenState();

        // abort previous
        if (STATE.abort) safe(() => STATE.abort.abort());
        STATE.abort = new AbortController();
        const signal = STATE.abort.signal;
        const myReq = ++STATE.reqId;

        STATE.params = params;

        setLoading(true);

        const sections = getSections();
        if (!sections.length) {
            setLoading(false);
            return;
        }

        try {
            // fetch all sections (parallel)
            const htmlList = await Promise.all(sections.map((sid) => fetchSection(sid, params, signal)));

            // if another request started, ignore
            if (myReq !== STATE.reqId) return;

            // Apply patches in order (last wins)
            htmlList.forEach((html) => {
                const doc = new DOMParser().parseFromString(html, "text/html");
                patchHTML(doc);
            });

            // restore open states AFTER patch
            restoreOpenState();

            // iOS Safari kick (observers start without scroll)
            raf2(() => {
                // force reflow
                document.body.offsetHeight;

                raf2(() => {
                    setLoading(false);

                    if (updateHistory) updateURL(params);

                    // custom elements re-upgrade (safe)
                    safe(() => {
                        const ff = document.querySelector("facet-filters-form");
                        ff && customElements.upgrade(ff);
                        document.querySelectorAll("price-range, facet-remove").forEach((el) => {
                            try { customElements.upgrade(el); } catch (_) { }
                        });
                    });

                    // fire ONE consistent event set
                    document.dispatchEvent(new CustomEvent("collection:updated"));
                    document.dispatchEvent(new CustomEvent("beseen:collection:reset", { detail: { source: "facets" } }));
                });
            });
        } catch (e) {
            if (e?.name === "AbortError") return;
            console.error("[beseen Facets] render error:", e);
            setLoading(false);
        }
    };

    /* -----------------------------
      FacetFiltersForm element (Dawn-like)
    ----------------------------- */
    class FacetFiltersForm extends HTMLElement {
        connectedCallback() {
            if (this.__bound) return;
            this.__bound = true;

            const form = this.querySelector("form");
            if (!form) return;

            const onChange = debounce((ev) => {
                safe(() => ev?.preventDefault?.());
                render(collectParams(), { updateHistory: true });
            }, 240);

            form.addEventListener("input", onChange);
            form.addEventListener("change", onChange);
        }

        onActiveFilterClick(params) {
            render((params || "").trim(), { updateHistory: true });
        }
    }

    class FacetRemove extends HTMLElement {
        connectedCallback() {
            if (this.__bound) return;
            this.__bound = true;

            const link = this.querySelector("a");
            if (!link) return;

            link.setAttribute("role", "button");

            const run = (e) => {
                safe(() => e.preventDefault());
                const params = (link.href.split("?")[1] || "").trim();
                const ff = document.querySelector("facet-filters-form");
                ff?.onActiveFilterClick?.(params);
            };

            link.addEventListener("click", run);
            link.addEventListener("keyup", (e) => {
                if (e.key === " " || e.code === "Space") run(e);
            });
        }
    }

    /* -----------------------------
      PriceRange (your markup safe)
    ----------------------------- */
    class PriceRange extends HTMLElement {
        connectedCallback() {
            if (this.__bound) return;
            this.__bound = true;

            this.textMin = this.querySelector(".price-input-min");
            this.textMax = this.querySelector(".price-input-max");
            this.rangeMin = this.querySelector(".range-min");
            this.rangeMax = this.querySelector(".range-max");
            this.track = this.querySelector(".price-slider-connect");
            this.minLabel = this.querySelector(".min-range-price");
            this.maxLabel = this.querySelector(".max-range-price");

            if (!this.textMin || !this.textMax || !this.rangeMin || !this.rangeMax || !this.track) return;

            this._lock = false;
            this._queued = false;

            this.rangeMin.addEventListener("input", () => this.onSlider());
            this.rangeMax.addEventListener("input", () => this.onSlider());
            this.textMin.addEventListener("change", () => this.onText());
            this.textMax.addEventListener("change", () => this.onText());

            this.syncFromInputs();
            this.updateVisuals();
        }

        maxCap() {
            const v = parseInt(this.rangeMax.getAttribute("max") || "0", 10);
            return Number.isFinite(v) && v > 0 ? v : 1;
        }

        clamp(min, max) {
            const cap = this.maxCap();
            min = Math.max(0, Math.min(min, cap));
            max = Math.max(0, Math.min(max, cap));
            if (max < min) max = min;
            const GAP = 100; // keep small gap
            if (max - min < GAP) max = Math.min(cap, min + GAP);
            return { min, max };
        }

        syncFromInputs() {
            const cap = this.maxCap();
            const rawMin = (this.textMin.value || "").replace(/,/g, "");
            const rawMax = (this.textMax.value || "").replace(/,/g, "");

            let min = parseInt(rawMin || "0", 10);
            let max = parseInt(rawMax || String(cap), 10);

            if (!Number.isFinite(min)) min = 0;
            if (!Number.isFinite(max)) max = cap;

            ({ min, max } = this.clamp(min, max));
            this.rangeMin.value = String(min);
            this.rangeMax.value = String(max);
        }

        updateVisuals() {
            const cap = this.maxCap();
            const min = parseInt(this.rangeMin.value || "0", 10);
            const max = parseInt(this.rangeMax.value || String(cap), 10);

            const p1 = (min / cap) * 100;
            const p2 = (max / cap) * 100;

            this.track.style.left = p1 + "%";
            this.track.style.width = (p2 - p1) + "%";

            if (this.minLabel) this.minLabel.textContent = `$ ${min}`;
            if (this.maxLabel) this.maxLabel.textContent = `$ ${max}`;
        }

        submitOnce() {
            if (this._queued) return;
            this._queued = true;

            requestAnimationFrame(() => {
                this._queued = false;
                safe(() => this.textMin.dispatchEvent(new Event("change", { bubbles: true })));
                safe(() => this.textMax.dispatchEvent(new Event("change", { bubbles: true })));
            });
        }

        onSlider() {
            if (this._lock) return;
            this._lock = true;

            let min = parseInt(this.rangeMin.value || "0", 10);
            let max = parseInt(this.rangeMax.value || String(this.maxCap()), 10);

            if (!Number.isFinite(min)) min = 0;
            if (!Number.isFinite(max)) max = this.maxCap();

            ({ min, max } = this.clamp(min, max));

            this.rangeMin.value = String(min);
            this.rangeMax.value = String(max);

            this.textMin.value = String(min);
            this.textMax.value = String(max);

            this.updateVisuals();
            this.submitOnce();

            requestAnimationFrame(() => (this._lock = false));
        }

        onText() {
            if (this._lock) return;
            this._lock = true;

            const cap = this.maxCap();
            const rawMin = (this.textMin.value || "").replace(/,/g, "");
            const rawMax = (this.textMax.value || "").replace(/,/g, "");

            // reset case (both blank)
            if (!rawMin && !rawMax) {
                this.rangeMin.value = "0";
                this.rangeMax.value = String(cap);
                this.updateVisuals();
                this.submitOnce();
                requestAnimationFrame(() => (this._lock = false));
                return;
            }

            let min = parseInt(rawMin || "0", 10);
            let max = parseInt(rawMax || String(cap), 10);

            if (!Number.isFinite(min)) min = 0;
            if (!Number.isFinite(max)) max = cap;

            ({ min, max } = this.clamp(min, max));

            this.rangeMin.value = String(min);
            this.rangeMax.value = String(max);

            // keep blanks if user left blank
            this.textMin.value = rawMin ? String(min) : "";
            this.textMax.value = rawMax ? String(max) : "";

            this.updateVisuals();
            this.submitOnce();

            requestAnimationFrame(() => (this._lock = false));
        }
    }

    /* -----------------------------
      Global delegated clicks (chips + clear-all)
    ----------------------------- */
    const bindDelegatesOnce = () => {
        if (window.__beseenFacetsBound) return;
        window.__beseenFacetsBound = true;

        document.addEventListener("click", (e) => {
            // chip remove + clear all (desktop + mobile)
            const a = e.target.closest(".active-facets a, .active-facets__clear a, .mobile-facets__clear");
            if (!a) return;
            if (!a.href) return;

            // allow other links
            if (!a.href.includes("?")) return;

            e.preventDefault();
            const params = (a.href.split("?")[1] || "").trim();
            const ff = document.querySelector("facet-filters-form");
            if (ff?.onActiveFilterClick) ff.onActiveFilterClick(params);
            else window.location.href = a.href;
        });

        // popstate (back/forward)
        window.addEventListener("popstate", (event) => {
            const params = event.state?.searchParams ?? window.location.search.replace(/^\?/, "");
            if (params !== STATE.params) render(params, { updateHistory: false });
        });
    };

    /* -----------------------------
      Mobile drawer — body scroll lock
    ----------------------------- */
    const bindMobileDrawer = () => {
        const drawer = document.querySelector(SEL.mobileDrawerDetails);
        if (!drawer || drawer.__drawerBound) return;
        drawer.__drawerBound = true;

        drawer.addEventListener("toggle", () => {
            if (drawer.open) {
                document.body.style.overflow = "hidden";
            } else {
                document.body.style.overflow = "";
            }
        });
    };

    /* -----------------------------
      Sort overlay wiring (your custom overlay)
    ----------------------------- */
    const bindSortOverlay = () => {
        const overlay = document.querySelector(SEL.sortOverlay);
        if (!overlay) return;

        if (overlay.__bound) return;
        overlay.__bound = true;

        const select = document.querySelector(SEL.sortSelect);
        const closeBtn = document.querySelector(SEL.sortCloseButton);
        const openBtn = document.querySelector(SEL.sortOpenButton);

        const mobileOnly = window.matchMedia("(max-width: 749px)");

        const open = () => {
            if (!mobileOnly.matches) return;
            overlay.style.display = "flex";
            requestAnimationFrame(() => overlay.classList.add("visible"));
            document.body.style.overflow = "hidden";
        };

        const close = () => {
            overlay.classList.remove("visible");
            setTimeout(() => {
                overlay.style.display = "none";
                document.body.style.overflow = "";
            }, 250);
        };

        openBtn?.addEventListener("click", (e) => {
            e.preventDefault();
            open();
        });
        closeBtn?.addEventListener("click", (e) => {
            e.preventDefault();
            close();
        });

        overlay.addEventListener("click", (e) => {
            // click outside inner panel closes
            if (e.target === overlay) close();
        });

        overlay.querySelectorAll(SEL.sortOption).forEach((opt) => {
            opt.addEventListener("click", (e) => {
                e.preventDefault();
                if (!mobileOnly.matches) return;

                const value = opt.dataset.value;
                if (!value) return;

                // update select (Dawn compatible)
                if (select) select.value = value;

                // highlight
                overlay.querySelectorAll(SEL.sortOption).forEach((o) => o.classList.remove("selected"));
                opt.classList.add("selected");

                // run facets render using merged params
                render(collectParams(), { updateHistory: true });
                close();
            });
        });
    };

    /* -----------------------------
      Boot (define + initial bindings)
    ----------------------------- */
    const defineSafely = (name, klass) => {
        if (!customElements.get(name)) customElements.define(name, klass);
    };

    safe(() => {
        defineSafely("facet-filters-form", FacetFiltersForm);
        defineSafely("facet-remove", FacetRemove);
        defineSafely("price-range", PriceRange);

        bindDelegatesOnce();
        bindSortOverlay();
        bindMobileDrawer();

        // After every update, re-bind overlay + mobile drawer (DOM replaced)
        document.addEventListener("collection:updated", () => {
            requestIdleCallback(bindSortOverlay, { timeout: 300 });
            requestIdleCallback(bindMobileDrawer, { timeout: 300 });
        });
    });
})();

/* =======================================================
   ✅ FINAL STICKY FACETS — SUBCATEGORY DRIVEN (NO GAP)
   Header & Subcategory handled by StickyHeader ONLY
========================================================== */
(() => {
    const facets = document.querySelector(".facets-wrapper");
    const subcategory = document.querySelector(".subcategory-navigation");
    const subcategoryNav = subcategory.querySelector(".subcategory-navigation__inner");
    const header = document.querySelector(".section-header");

    if (!facets || !subcategory) return;

    // let isDesktop = window.matchMedia("(min-width: 750px)").matches;
    let ticking = false;

    // 🔒 Capture document position ONCE
    const subcategoryDocTop =
        subcategory.getBoundingClientRect().top + window.scrollY;

    const updateSticky = () => {
        // if (!isDesktop) return;

        const scrollTop = window.scrollY || window.pageYOffset;

        let triggerPoint = 0;
        let top = 0;


        // ✅ CONDITION 1: subcategory INNER EXISTS
        if (subcategoryNav !== null && subcategoryNav.offsetHeight > 0) {

            // Trigger after subcategory finishes
            const subDocTop =
                subcategory.offsetTop + subcategory.offsetHeight;

            triggerPoint = subDocTop;

            // Top = header + full subcategory
            top = header.offsetHeight + subcategory.offsetHeight;

        }
        // ✅ CONDITION 2: NO inner nav → header only
        else {

            triggerPoint =
                header.offsetTop;

            top = header.offsetHeight;
        }

        if (scrollTop >= triggerPoint) {
            facets.classList.add("is-fixed"); // use custom class
            facets.style.top = `${top}px`;
        } else {
            facets.classList.remove("is-fixed");
            facets.style.top = "";
        }
    };



    const rafScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
            updateSticky();
            ticking = false;
        });
    };

    window.addEventListener("scroll", rafScroll, { passive: true });
    window.addEventListener("resize", () => {
        // isDesktop = window.matchMedia("(min-width: 750px)").matches;
        updateSticky();
    });

    // 🔥 After AJAX filter update
    document.addEventListener("collection:updated", () => {
        requestAnimationFrame(updateSticky);
    });

    // initial
    requestAnimationFrame(updateSticky);
})();
